import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { User } from 'firebase/auth';

interface ExtensionAuthHandlerProps {
  user: User;
}

export default function ExtensionAuthHandler({ user }: ExtensionAuthHandlerProps) {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'sending' | 'success' | 'error'>('sending');
  const extensionId = searchParams.get('extensionId');

  useEffect(() => {
    if (!extensionId) {
      setStatus('error');
      return;
    }

    // Store extension ID for future communication
    localStorage.setItem('extensionId', extensionId);

    const sendAuthToExtension = async () => {
      try {
        // Get the ID token and refresh token for authenticated API calls
        const idToken = await user.getIdToken();
        // Get refresh token from user object (Firebase stores this internally)
        const refreshToken = (user as any).stsTokenManager?.refreshToken;

        const credentials = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          idToken,
          refreshToken, // Include refresh token for automatic token refresh
        };

        if (window.chrome?.runtime?.sendMessage) {
          window.chrome.runtime.sendMessage(
            extensionId,
            { type: 'AUTH_FROM_WEB_APP', credentials },
            (response) => {
              if (response?.success) {
                setStatus('success');
              } else {
                console.error('Failed to send credentials to extension:', response?.error);
                setStatus('error');
              }
            }
          );
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('Error sending credentials to extension:', err);
        setStatus('error');
      }
    };

    sendAuthToExtension();
  }, [extensionId, user]);

  if (status === 'sending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Connecting to Extension...
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Please wait while we connect your account.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Extension Connected!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You're now signed in. You can close this tab and return to the extension.
          </p>
          <button
            onClick={() => window.close()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Close Tab
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 w-full max-w-md text-center">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Connection Failed
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Could not connect to the extension. Please make sure the extension is installed and try again.
        </p>
        <button
          onClick={() => window.location.href = '/'}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}
